import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import FavoriteRounded from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderRounded from '@mui/icons-material/FavoriteBorderRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import { fmt, p2Necessaria } from '@/features/notas/calculo';
import { primeiroNome } from '@/features/notas/texto';
import type { DisciplinaDesempenho } from '@/features/notas/types';

/**
 * POO · Silksong em Java: a P1 vira a ficha da Heroína (máscaras = pontos da P1, seda = cada componente) e a P2 vira
 * o chefe, com a vida igual à nota que falta para fechar a média sem P3. O duelo é um do/while por turnos.
 */

const ATAQUES = [
  { id: 'T2', nome: 'Investida AWS', arma: 'atividades AWS (T2)', peso: 0.3 },
  { id: 'IND2', nome: 'Agulha individual', arma: 'apresentação individual', peso: 0.5 },
  { id: 'GRU2', nome: 'Ataque em grupo', arma: 'projeto do grupo', peso: 0.2 },
] as const;

function Console({ linhas }: { linhas: string[] }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        minHeight: 120,
        maxHeight: 260,
        overflowY: 'auto',
        borderRadius: 2,
        bgcolor: '#140E26',
        color: '#D4F45E',
        fontSize: 12.5,
        whiteSpace: 'pre-wrap',
      }}
    >
      {linhas.join('\n')}
    </Box>
  );
}

export function FichaHeroina({ d, nome }: { d: DisciplinaDesempenho; nome: string }) {
  const p1 = d.notas.P1 ?? 0;
  const heroina = primeiroNome(nome) || 'Hornet';
  // sem limite em 10: com P1 abaixo de 2 o chefe tem mais vida do que a P2 consegue tirar (a P3 é inevitável)
  const vidaChefe = Math.round(p2Necessaria(p1) * 100) / 100;
  const [forca, setForca] = useState<Record<string, number>>({
    T2: d.notas.T1 ?? 7,
    IND2: d.notas.IND1 ?? 7,
    GRU2: d.notas.GRU1 ?? 7,
  });
  const [turno, setTurno] = useState(0);
  const [dano, setDano] = useState(0);
  const [usados, setUsados] = useState<string[]>([]);
  const [log, setLog] = useState<string[]>([
    `Heroina ${heroina.toLowerCase()} = new Heroina("${heroina}", ${Math.round(p1)});`,
    '> Um chefe se aproxima: P2.',
  ]);
  const vidaRestante = Math.max(0, vidaChefe - dano);
  const acabou = vidaRestante <= 0 || usados.length === ATAQUES.length;

  const ataca = (a: (typeof ATAQUES)[number]) => {
    const golpe = a.peso * (forca[a.id] ?? 0);
    const novoDano = dano + golpe;
    const novoTurno = turno + 1;
    const linhas = [
      `Turno ${novoTurno}`,
      `${heroina} usa ${a.nome} (${a.arma}, nota ${fmt(forca[a.id], 1)}): ${fmt(golpe)} de dano!`,
    ];
    if (vidaChefe - novoDano <= 0)
      linhas.push(`Vitória sobre P2! Média (P1 + P2) / 2 ≥ 6 sem precisar da P3.`, 'Fim de jogo.');
    else if (usados.length + 1 === ATAQUES.length)
      linhas.push(
        `P2 ainda tem ${fmt(vidaChefe - novoDano)} de vida... a P3 te espera.`,
        'Fim de jogo.',
      );
    else linhas.push(`P2 tem ${fmt(vidaChefe - novoDano)} de vida.`);
    setTurno(novoTurno);
    setDano(novoDano);
    setUsados((u) => [...u, a.id]);
    setLog((l) => [...l, ...linhas]);
  };

  const recomeca = () => {
    setTurno(0);
    setDano(0);
    setUsados([]);
    setLog([
      `Heroina ${heroina.toLowerCase()} = new Heroina("${heroina}", ${Math.round(p1)});`,
      '> Um chefe se aproxima: P2.',
    ]);
  };

  const mascaras = Array.from({ length: 10 }, (_, i) => Math.max(0, Math.min(1, p1 - i)));
  const seda = [
    { nome: 'AWS (T1)', v: d.notas.T1 ?? 0, peso: 0.3 },
    { nome: 'Individual', v: d.notas.IND1 ?? 0, peso: 0.5 },
    { nome: 'Grupo', v: d.notas.GRU1 ?? 0, peso: 0.2 },
  ];

  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            A ficha da sua Heroína
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Como na prova: a sua P1 vira máscaras de vida (cada ponto é uma máscara) e cada
            componente vira um carretel de seda.
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Máscaras: {fmt(p1, 1)} de 10
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                {mascaras.map((m, i) => (
                  <Box key={i} sx={{ position: 'relative', width: 30, height: 30 }} aria-hidden>
                    <FavoriteBorderRounded
                      sx={{ position: 'absolute', inset: 0, fontSize: 30, color: 'text.disabled' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        overflow: 'hidden',
                        width: `${m * 100}%`,
                      }}
                    >
                      <FavoriteRounded sx={{ fontSize: 30, color: 'primary.main' }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
              {seda.map((x) => (
                <Box key={x.nome} sx={{ mb: 1.25 }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      Seda · {x.nome}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        peso {fmt(x.peso, 1)}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {fmt(x.v)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={x.v * 10}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                System.out.println({heroina.toLowerCase()});
              </Typography>
              <Console
                linhas={[
                  `Heroina[nome=${heroina}, mascaras=${fmt(p1, 1)}, sedaAws=${fmt(d.notas.T1)}, sedaIndividual=${fmt(d.notas.IND1)}, sedaGrupo=${fmt(d.notas.GRU1)}]`,
                  '',
                  `// P1 = 0.3 * ${fmt(d.notas.T1)} + 0.5 * ${fmt(d.notas.IND1)} + 0.2 * ${fmt(d.notas.GRU1)}`,
                  `// P1 = ${fmt(d.notas.P1, 1)}`,
                ]}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            Duelo contra o chefe P2
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A vida do chefe é a nota que falta na P2 para a média (P1 + P2) / 2 chegar a 6. Ajuste a
            força de cada ataque (a nota que você imagina tirar) e ataque: cada golpe tira peso ×
            nota. Três ataques, como num do/while que termina quando o chefe cai ou os ataques
            acabam.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ width: 96 }}>
              P2 · vida
            </Typography>
            <LinearProgress
              variant="determinate"
              color={vidaRestante <= 0 ? 'success' : 'error'}
              value={vidaChefe ? (vidaRestante / vidaChefe) * 100 : 0}
              sx={{ flex: 1, height: 12, borderRadius: 6 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, width: 72, textAlign: 'right' }}>
              {fmt(vidaRestante)} / {fmt(vidaChefe)}
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            {ATAQUES.map((a) => (
              <Grid key={a.id} size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">{a.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.arma} · peso {fmt(a.peso, 1)}
                    </Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={10}
                      step={0.5}
                      value={forca[a.id]}
                      disabled={usados.includes(a.id) || acabou}
                      onChange={(_, v) => setForca((f) => ({ ...f, [a.id]: v as number }))}
                      aria-label={`Nota em ${a.arma}`}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      disabled={usados.includes(a.id) || acabou}
                      onClick={() => ataca(a)}
                    >
                      Atacar ({fmt(a.peso * (forca[a.id] ?? 0))} de dano)
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Console linhas={log} />
          </Box>
          <Button startIcon={<ReplayRounded />} onClick={recomeca} sx={{ mt: 1 }}>
            Novo duelo
          </Button>
        </CardContent>
      </Card>
    </Stack>
  );
}
